import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Square, Clock } from 'lucide-react';
import { useNotification } from '../hooks/useNotification';
import { authService } from '../services/auth';

interface PomodoroTimerProps {
  isCompact?: boolean;
  selectedTeamId?: string | null;
}

export const PomodoroTimer: React.FC<PomodoroTimerProps> = ({ 
  isCompact = false,
  selectedTeamId = null
}) => {
  const { showNotification } = useNotification();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get user's preferred pomodoro duration for current context (team-aware)
  const getUserPomodoroMinutes = (): number => {
    const user = authService.getUser();
    const contextKey = selectedTeamId || 'personal';
    
    // Check if pomodoroDefaultMinutes is already context-aware
    if (user?.preferences?.pomodoroDefaultMinutes && typeof user.preferences.pomodoroDefaultMinutes === 'object') {
      return user.preferences.pomodoroDefaultMinutes[contextKey] || 25;
    }
    
    // Fallback for legacy format (single number)
    return user?.preferences?.pomodoroDefaultMinutes || 25;
  };

  const defaultMinutes = getUserPomodoroMinutes();
  const [minutes, setMinutes] = useState(defaultMinutes);
  const [seconds, setSeconds] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(defaultMinutes * 60);
  const [timeLeft, setTimeLeft] = useState(defaultMinutes * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Update timer when user preferences or team context changes
  useEffect(() => {
    const currentPrefMinutes = getUserPomodoroMinutes();
    if (!isRunning && !isEditing && currentPrefMinutes !== minutes) {
      setMinutes(currentPrefMinutes);
      const newTotalSeconds = currentPrefMinutes * 60;
      setTotalSeconds(newTotalSeconds);
      setTimeLeft(newTotalSeconds);
    }
  }, [authService.getUser()?.preferences?.pomodoroDefaultMinutes, selectedTeamId, isRunning, isEditing, minutes]);

  // Timer logic
  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            showNotification('success', 'Timer completed!');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, timeLeft, showNotification]);

  const formatTime = (totalSeconds: number): string => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Save user's preferred pomodoro duration for current context (team-aware)
  const savePomodoroPreference = async (newMinutes: number) => {
    try {
      const user = authService.getUser();
      if (user) {
        const contextKey = selectedTeamId || 'personal';
        
        // Ensure pomodoroDefaultMinutes is an object for context-aware storage
        let pomodoroSettings = user.preferences?.pomodoroDefaultMinutes || {};
        
        // Convert legacy format (single number) to new format (object)
        if (typeof pomodoroSettings === 'number') {
          pomodoroSettings = { personal: pomodoroSettings };
        }
        
        // Update the setting for the current context
        pomodoroSettings[contextKey] = newMinutes;
        
        const updatedPreferences = {
          ...user.preferences,
          pomodoroDefaultMinutes: pomodoroSettings
        };
        
        await authService.updateUserSettings({ preferences: updatedPreferences });
      }
    } catch (error) {
      console.error('Failed to save pomodoro preference:', error);
    }
  };

  const handleTimeClick = () => {
    if (!isRunning) {
      setIsEditing(true);
    }
  };

  const handleTimeSubmit = () => {
    const newTotalSeconds = minutes * 60 + seconds;
    setTotalSeconds(newTotalSeconds);
    setTimeLeft(newTotalSeconds);
    setIsEditing(false);
    
    // Save the new duration preference (only if minutes changed from default)
    if (minutes !== getUserPomodoroMinutes()) {
      savePomodoroPreference(minutes);
    }
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(0, Math.min(99, parseInt(e.target.value) || 0));
    setMinutes(value);
  };

  const handleSecondsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
    setSeconds(value);
  };

  const handleStart = () => {
    setIsRunning(true);
  };

  const handlePause = () => {
    setIsRunning(false);
  };

  const handleStop = () => {
    setIsRunning(false);
    setTimeLeft(totalSeconds);
  };

  if (isCompact) {
    return (
      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
        <Clock className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-mono font-medium">
          {formatTime(timeLeft)}
        </span>
        <div className="flex gap-1">
          {isRunning ? (
            <button
              onClick={handlePause}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <Pause className="w-3 h-3" />
            </button>
          ) : (
            <button
              onClick={handleStart}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <Play className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={handleStop}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <Square className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-center mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold">Pomodoro Timer</h3>
        </div>
      </div>

      <div className="text-center mb-6">
        {isEditing ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2">
              <input
                type="number"
                min="0"
                max="99"
                value={minutes}
                onChange={handleMinutesChange}
                className="w-16 text-2xl font-mono text-center border border-gray-300 rounded px-2 py-1"
              />
              <span className="text-2xl font-mono">:</span>
              <input
                type="number"
                min="0"
                max="59"
                value={seconds}
                onChange={handleSecondsChange}
                className="w-16 text-2xl font-mono text-center border border-gray-300 rounded px-2 py-1"
              />
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={handleTimeSubmit}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Set Time
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className="text-4xl font-mono font-bold text-gray-800 cursor-pointer hover:text-blue-600 transition-colors"
            onClick={handleTimeClick}
            title="Click to edit time"
          >
            {formatTime(timeLeft)}
          </div>
        )}
      </div>

      {!isEditing && (
        <div className="flex justify-center gap-3">
          {isRunning ? (
            <button
              onClick={handlePause}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            >
              <Pause className="w-4 h-4" />
              Pause
            </button>
          ) : (
            <button
              onClick={handleStart}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              <Play className="w-4 h-4" />
              Start
            </button>
          )}
          <button
            onClick={handleStop}
            className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            <Square className="w-4 h-4" />
            Stop
          </button>
        </div>
      )}
    </div>
  );
};